import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { adminManagementService } from '../../services/adminService';
import {
    ChevronLeft,
    Activity,
    AlertCircle,
    Clock,
    Settings,
    FileText,
    Calendar,
    Eye
} from 'lucide-react';
import './AdminPages.css';
import './AdminWorkspaceDetail.css'; // Reuse workspace detail styling for cards/grids

function AdminGoalDetail() {
    const { id } = useParams();
    const [goalData, setGoalData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadGoalDetail = async () => {
            try {
                setLoading(true);
                const data = await adminManagementService.getGoalDetail(id);
                setGoalData(data);
            } catch (err) {
                setError('Failed to load test goal details');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        loadGoalDetail();
    }, [id]);

    if (loading) return <div className="admin-loading">Loading test goal details...</div>;
    if (error || !goalData || !goalData.goal) {
        return (
            <div className="admin-page">
                <div className="admin-breadcrumb">
                    <Link to="/admin/goals">
                        <ChevronLeft size={16} />
                        Back to Test Goals
                    </Link>
                </div>
                <div className="admin-form-error">
                    <AlertCircle size={18} /> {error || 'Test goal not found'}
                </div>
            </div>
        );
    }

    const { goal, appName } = goalData;

    // Helper to format duration
    const formatDuration = (ms) => {
        if (!ms) return 'N/A';
        const seconds = Math.floor(ms / 1000);
        if (seconds < 60) return `${seconds}s`;
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}m ${remainingSeconds}s`;
    };

    // Helper for execution status badge class
    const getStatusClass = (status) => {
        switch (status) {
            case 'success': return 'success';
            case 'failed': return 'danger';
            case 'error': return 'danger';
            case 'running': return 'warning';
            case 'pending': return 'warning';
            case 'queued': return 'warning';
            case 'cancelled': return 'warning';
            default: return 'warning';
        }
    };

    return (
        <div className="admin-page">
            <div className="admin-breadcrumb">
                <Link to="/admin/goals">
                    <ChevronLeft size={16} />
                    Back to Test Goals
                </Link>
            </div>

            <div className="workspace-detail-header" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                    <div>
                        <h1 style={{ fontSize: '1.6rem', color: '#f8fafc', margin: 0 }}>
                            Goal #{goal.id.substring(0, 8)} details
                        </h1>
                        <p className="workspace-id" style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>Full ID: {goal.id}</p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <span className={`plan-badge ${goal.platform === 'android' ? 'pro' : 'business'}`}>
                            {goal.platform.toUpperCase()}
                        </span>
                        <span className={`status-badge ${goal.status === 'completed' ? 'success' : 'warning'}`}>
                            {goal.status.toUpperCase()}
                        </span>
                    </div>
                </div>
                
                <div style={{ width: '100%', maxWidth: '800px', marginTop: '8px' }}>
                    <span style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Goal Text</span>
                    <textarea
                        readOnly
                        value={goal.goalText || 'No Goal Text'}
                        style={{
                            width: '100%',
                            minHeight: '60px',
                            maxHeight: '120px',
                            backgroundColor: '#0f172a',
                            border: '1px solid #334155',
                            borderRadius: '6px',
                            padding: '8px 12px',
                            color: '#cbd5e1',
                            fontSize: '0.85rem',
                            resize: 'vertical',
                            outline: 'none',
                            fontFamily: 'inherit',
                            lineHeight: '1.4'
                        }}
                    />
                </div>
            </div>

            <div className="detail-grid">
                {/* Goal Metadata Card */}
                <div className="detail-card">
                    <div className="card-header">
                        <FileText size={20} />
                        <h3>Goal Metadata</h3>
                    </div>
                    <div className="card-body">
                        <div className="info-row">
                            <span className="info-label">Workspace:</span>
                            <span className="info-value">
                                <Link to={`/admin/workspaces/${goal.workspace?.id}`} className="admin-link" style={{ color: '#38bdf8', textDecoration: 'none' }}>
                                    {goal.workspace?.name || 'Unknown Workspace'}
                                </Link>
                            </span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">Creator:</span>
                            <span className="info-value">
                                {goal.creator ? (
                                    <span>{goal.creator.fullName || goal.creator.email} ({goal.creator.email})</span>
                                ) : (
                                    'System / Integration'
                                )}
                            </span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">Created At:</span>
                            <span className="info-value">{new Date(goal.createdAt).toLocaleString()}</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">Priority:</span>
                            <span className="info-value" style={{ textTransform: 'capitalize' }}>
                                {goal.priority || 'Normal'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Application Config Card */}
                <div className="detail-card">
                    <div className="card-header">
                        <Settings size={20} />
                        <h3>Application Config</h3>
                    </div>
                    <div className="card-body">
                        <div className="info-row">
                            <span className="info-label">App Target:</span>
                            <span className="info-value">{appName}</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">App ID:</span>
                            <span className="info-value" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                {goal.appConfig?.appId || 'N/A'}
                            </span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">Platform:</span>
                            <span className="info-value" style={{ textTransform: 'uppercase' }}>
                                {goal.platform}
                            </span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">Credentials Configured:</span>
                            <span className="info-value">
                                {goal.appConfig?.credentials ? 'Yes' : 'No'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Raw Config Card */}
                {goal.appConfig && (
                    <div className="detail-card full-width">
                        <div className="card-header">
                            <Settings size={20} />
                            <h3>App Config Properties (JSON)</h3>
                        </div>
                        <div className="card-body">
                            <pre className="metadata-pre">
                                {JSON.stringify(goal.appConfig, null, 2)}
                            </pre>
                        </div>
                    </div>
                )}
            </div>

            {/* Run History Section */}
            <div className="detail-section">
                <div className="section-header">
                    <Activity size={20} />
                    <h3>Run & Execution History ({goal.executions?.length || 0})</h3>
                </div>
                <div className="admin-table-container">
                    <table className="admin-table">
                        <thead>
                            <tr>
                                <th>Run ID</th>
                                <th>Status</th>
                                <th>Trigger Source</th>
                                <th>Duration</th>
                                <th>Started At</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {goal.executions && goal.executions.length > 0 ? (
                                goal.executions.map(run => (
                                    <tr key={run.id}>
                                        <td>
                                            <code style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                                                {run.id}
                                            </code>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${getStatusClass(run.status)}`}>
                                                {run.status.toUpperCase()}
                                            </span>
                                        </td>
                                        <td>
                                            <span style={{ textTransform: 'capitalize' }}>
                                                {run.triggerSource || 'manual'}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <Clock size={14} color="#64748b" />
                                                <span>{formatDuration(run.duration)}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <Calendar size={14} color="#64748b" />
                                                <span>{new Date(run.createdAt).toLocaleString()}</span>
                                            </div>
                                        </td>
                                        <td>
                                            <Link 
                                                to={`/admin/runs/${run.id}`} 
                                                className="admin-link"
                                                style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#38bdf8', textDecoration: 'none', fontWeight: 600 }}
                                            >
                                                <Eye size={14} /> View Run
                                            </Link>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="empty-state">No execution history found for this goal</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default AdminGoalDetail;
